import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@os/components/ui/card";
import { Badge } from "@os/components/ui/badge";
import { Button } from "@os/components/ui/button";
import { Textarea } from "@os/components/ui/textarea";
import { Progress } from "@os/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@os/components/ui/tooltip";
import { Laptop, Zap, Cpu, CheckCircle2, TriangleAlert, Cloud, Copy, Eraser, Play, RefreshCw, Info, ExternalLink, Pencil } from "lucide-react";
import { useLocalLLM } from "@/contexts/LocalLLMContext";
import { cn } from "@os/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@os/components/ui/collapsible";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import SyntaxHighlighter from "@/components/code/SyntaxHighlighter";
import LinkPreview from "@/components/common/LinkPreview";
import { cleanMarkdownBold } from "@/lib/utils";

const StatePill: React.FC<{ state: string }> = ({ state }) => {
  const conf = useMemo(() => {
    switch (state) {
      case "ready": return { label: "Ready", className: "bg-emerald-600 text-white" };
      case "downloading": return { label: "Downloading", className: "bg-blue-600 text-white" };
      case "initializing": return { label: "Initializing", className: "bg-indigo-600 text-white" };
      case "webgpu_not_supported": return { label: "WebGPU Unavailable", className: "bg-amber-600 text-white" };
      case "error": return { label: "Error", className: "bg-red-600 text-white" };
      default: return { label: "Idle", className: "bg-muted text-foreground" };
    }
  }, [state]);
  return <span className={cn("px-2 py-0.5 rounded text-xs font-medium", conf.className)}>{conf.label}</span>;
};

const StatusRibbon: React.FC<{
  gpuName?: string;
  state: string;
  progress?: { loaded: number; total: number; stage?: string };
}> = ({ gpuName, state, progress }) => {
  return (
    <div className="flex items-center justify-between rounded-md border bg-gradient-to-r from-primary/20 via-primary/10 to-background px-3 py-2">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">FreeMapleAI (Local)</span>
        <StatePill state={state} />
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Cpu className="h-3 w-3" />
        <span className="truncate max-w-[220px]" title={gpuName || "GPU: Unknown"}>
          {gpuName ? `GPU: ${gpuName}` : "GPU: Unknown"}
        </span>
      </div>
    </div>
  );
};

const FreeMapleAI: React.FC = () => {
  const { status, isAvailable, generate } = useLocalLLM();
  const [prompt, setPrompt] = useState("");
  const [stream, setStream] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [tokenCount, setTokenCount] = useState(0);
  const [tps, setTps] = useState<number | null>(null);
  const warmupTimer = useRef<number | null>(null);
  const [showWarmupHint, setShowWarmupHint] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // NEW: allow editing of the streamed output
  const [isEditingOutput, setIsEditingOutput] = useState(false);

  // NEW: CDN reachability test state
  const [cdnTestResult, setCdnTestResult] = useState<'unknown' | 'ok' | 'blocked'>('unknown');
  const [cdnTestMessage, setCdnTestMessage] = useState<string | null>(null);

  const isIframe = typeof window !== "undefined" ? window.self !== window.top : false;

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [stream]);

  useEffect(() => {
    return () => {
      if (warmupTimer.current) {
        clearTimeout(warmupTimer.current);
        warmupTimer.current = null;
      }
    };
  }, []);

  const gpuName = status.webGpuInfo?.adapterName || (status.diagnostics?.hasWebGPU ? "Detected" : "Unknown");
  const progressPct = status.progress ? Math.max(0, Math.min(100, status.progress.loaded)) : 0;

  const samplePrompts = [
    "Summarize: What is MapleOS and how does it keep AI private?",
    "Create a 3-point plan for organizing a client workspace.",
    "Write a concise product blurb about FreeMapleAI (on-device AI)."
  ];

  const handleRecheck = () => {
    import("@/llm/localTinyLlamaManager").then(m => m.localLLM.init());
  };

  const openStandalone = () => {
    const base = `${window.location.origin}`;
    const url = `${base}/desktop/start?open=free-maple-ai&title=FreeMapleAI`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // NEW: Test CDN access (detect blockers)
  const handleCdnTest = () => {
    setCdnTestMessage(null);
    setCdnTestResult('unknown');
    const img = new Image();
    const start = Date.now();
    img.onload = () => {
      const ms = Date.now() - start;
      setCdnTestResult('ok');
      setCdnTestMessage(`CDN reachable (~${ms}ms).`);
    };
    img.onerror = () => {
      setCdnTestResult('blocked');
      setCdnTestMessage("CDN appears blocked by a network filter or extension.");
    };
    img.referrerPolicy = "no-referrer";
    img.src = "https://huggingface.co/front/assets/huggingface_logo-noborder.svg";
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;
    setStream("");
    setErrorMsg(null);
    setIsGenerating(true);
    setShowWarmupHint(false);
    setTokenCount(0);
    setTps(0); // CHANGED: start at 0 so UI shows "~0 tok/s" immediately

    const start = Date.now();

    warmupTimer.current = window.setTimeout(() => {
      if (tokenCount === 0) setShowWarmupHint(true);
    }, 15000);

    try {
      await generate({
        prompt,
        maxTokens: 256,
        onToken: (t) => {
          setStream((s) => s + t);
          setTokenCount((prev) => {
            const next = prev + 1;
            const elapsedSec = (Date.now() - start) / 1000;
            if (elapsedSec > 0) {
              setTps(Math.max(0, Math.round(next / elapsedSec))); // CHANGED: allow 0 tok/s
            }
            return next;
          });
        },
        onDone: () => {
          setIsGenerating(false);
          if (warmupTimer.current) {
            clearTimeout(warmupTimer.current);
            warmupTimer.current = null;
          }
        },
      });
    } catch (err: any) {
      setIsGenerating(false);
      if (warmupTimer.current) {
        clearTimeout(warmupTimer.current);
        warmupTimer.current = null;
      }
      setShowWarmupHint(false);
      setErrorMsg(String(err?.message ?? err));
    }
  };

  const handleCopy = () => {
    if (!stream) return;
    navigator.clipboard.writeText(stream);
  };

  const headerNote = useMemo(() => {
    if (status.state === "webgpu_not_supported") {
      return "Local mode requires a browser with WebGPU (e.g., recent Chrome/Edge on desktop). You can still use cloud models.";
    }
    if (status.state === "error") {
      return "Initialization failed. Try refreshing the app or using a cloud model.";
    }
    return "Local AI runs fully on your device. No prompts are sent to the cloud.";
  }, [status.state]);

  return (
    <div className="p-3 space-y-3" data-window-interactive="true">
      {/* Status */}
      <div className="flex items-center justify-between gap-2">
        <StatusRibbon gpuName={gpuName} state={status.state} progress={status.progress} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRecheck} className="shrink-0">
            <RefreshCw className="h-4 w-4 mr-1" /> Re-check
          </Button>
          <Button variant="outline" size="sm" onClick={handleCdnTest} className="shrink-0">
            <Cloud className="h-4 w-4 mr-1" /> Test CDN
          </Button>
          <Button variant="default" size="sm" onClick={openStandalone} className="shrink-0">
            <ExternalLink className="h-4 w-4 mr-1" /> Open Standalone
          </Button>
        </div>
      </div>
      {["downloading", "initializing"].includes(status.state) && (
        <div className="rounded-md border bg-muted/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground">
              {status.state === "downloading" ? (status.progress?.stage || "Preparing model...") : "Setting up local runtime..."}
            </div>
            <div className="text-xs">{progressPct}%</div>
          </div>
          <Progress value={progressPct} />
        </div>
      )}

      {/* Note */}
      <div className="text-xs text-muted-foreground">
        {status.state === "webgpu_not_supported"
          ? (isIframe
              ? "WebGPU looks blocked because MapleOS is inside an iframe. Use Open Standalone to launch in a new tab."
              : "Local mode requires WebGPU support in this browser context.")
          : "Local AI runs fully on your device. No prompts are sent to the cloud."}
      </div>

      {/* Diagnostics */}
      <Collapsible open={diagOpen} onOpenChange={setDiagOpen}>
        <Card className="border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" /> Diagnostics
              </CardTitle>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">{diagOpen ? "Hide" : "Show"}</Button>
              </CollapsibleTrigger>
            </div>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="text-xs text-muted-foreground">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <div>WebGPU: <span className={cn("font-medium", status.diagnostics?.hasWebGPU ? "text-emerald-600" : "text-red-600")}>
                  {status.diagnostics?.hasWebGPU ? "Available" : "Unavailable"}
                </span></div>
                <div>GPU: <span className="font-medium">{gpuName || "Unknown"}</span></div>
                <div>crossOriginIsolated: <span className="font-medium">{String(status.diagnostics?.crossOriginIsolated ?? false)}</span></div>
                <div>secureContext: <span className="font-medium">{String(status.diagnostics?.secureContext ?? false)}</span></div>
                <div>protocol: <span className="font-medium">{status.diagnostics?.protocol || "unknown"}</span></div>
                <div>inIframe: <span className="font-medium">{String(status.diagnostics?.isIframe ?? isIframe)}</span></div>
                <div>CDN access: <span className={cn("font-medium", cdnTestResult === 'ok' ? "text-emerald-600" : cdnTestResult === 'blocked' ? "text-red-600" : "text-muted-foreground")}>
                  {cdnTestResult === 'ok' ? "Reachable" : cdnTestResult === 'blocked' ? "Blocked" : "Unknown"}
                </span></div>
                {cdnTestMessage && <div className="col-span-2">{cdnTestMessage}</div>}
                {status.error && <div className="col-span-2 text-red-600 break-words">Error: {status.error}</div>}
              </div>

              {status.state === "webgpu_not_supported" && (
                <div className="mt-2">
                  {isIframe
                    ? "WebGPU is blocked by iframe permissions. Click Open Standalone to run MapleOS at the top level."
                    : "WebGPU isn't available in this context. Try Chrome/Edge desktop or check flags and driver support."}
                </div>
              )}
              {status.state === "error" && (
                <div className="mt-2">
                  Initialization failed. Click "Re-check" above to try again, or use "Open Standalone."
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Chat card */}
      <Card className="border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Local Chat</CardTitle>
            <div className="flex items-center gap-2">
              {isAvailable ? (
                <Badge variant="outline" className="gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                  Local AI Ready
                </Badge>
              ) : status.state === "webgpu_not_supported" ? (
                <Badge variant="outline" className="gap-1">
                  <TriangleAlert className="h-3 w-3 text-amber-600" />
                  WebGPU Unavailable
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <Cloud className="h-3 w-3 text-blue-600" />
                  Falling back to Cloud (if selected elsewhere)
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Prompt input */}
          <div className="space-y-2">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask something… (local, private, free)"
              className="min-h-[84px]"
              disabled={!isAvailable || isGenerating}
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {samplePrompts.map((p, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => setPrompt(p)}
                    disabled={isGenerating}
                  >
                    {idx === 0 ? "Sample 1" : idx === 1 ? "Sample 2" : "Sample 3"}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPrompt("")}
                        disabled={isGenerating}
                        aria-label="Clear prompt"
                      >
                        <Eraser className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Clear prompt</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button onClick={handleGenerate} disabled={!isAvailable || isGenerating}>
                  <Play className="h-4 w-4 mr-2" />
                  {isGenerating ? "Generating…" : "Generate locally"}
                </Button>
              </div>
            </div>
          </div>

          {/* Streamed output */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Response</div>
              <div className="flex gap-2 items-center text-xs text-muted-foreground">
                {(isGenerating || stream) && (
                  <span className="rounded bg-muted px-2 py-1">
                    Tokens: {tokenCount} {tps !== null ? `• ~${tps} tok/s` : ""} {/* CHANGED: show even when 0 */}
                  </span>
                )}
                {/* NEW: Edit/Done toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingOutput((v) => !v)}
                  disabled={isGenerating}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {isEditingOutput ? "Done" : "Edit"}
                </Button>
                {/* Existing Copy button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" onClick={handleCopy} disabled={!stream}>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy response to clipboard</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {isGenerating && showWarmupHint && tokenCount === 0 && (
              <div className="text-xs text-muted-foreground">
                First run warmup: compiling GPU kernels can take 10–30s. Tokens will stream once ready.
              </div>
            )}

            {errorMsg && (
              <div className="text-xs text-red-600">
                Local generation failed: {errorMsg}
              </div>
            )}

            <div
              ref={streamRef}
              className="min-h-[140px] max-h-[300px] overflow-auto rounded-md border bg-card p-3 text-sm leading-relaxed"
            >
              {stream ? (
                isEditingOutput ? (
                  // NEW: editable markdown text
                  <Textarea
                    value={stream}
                    onChange={(e) => setStream(e.target.value)}
                    className="min-h-[140px]"
                    disabled={isGenerating}
                    placeholder="Edit the response here…"
                  />
                ) : (
                  // Existing formatted preview
                  <div className="prose prose-sm dark:prose-invert max-w-none break-words">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        code({ node, inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || "");
                          return !inline && match ? (
                            <SyntaxHighlighter
                              code={String(children).replace(/\n$/, "")}
                              language={match[1]}
                            />
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                        a: ({ node, ...props }) => <LinkPreview url={props.href || ""} />,
                        img: ({ node, ...props }) => (
                          <img className="max-w-full h-auto rounded-md my-2" {...props} />
                        ),
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-6 my-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-6 my-2">{children}</ol>,
                      }}
                    >
                      {cleanMarkdownBold(stream)}
                    </ReactMarkdown>
                  </div>
                )
              ) : (
                <div className="text-muted-foreground text-sm">Output will stream here.</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FreeMapleAI;